import { memo } from 'react';
import AuthButton from './AuthButton';
import AuthPaper from './AuthPaper';
import { ShieldOutlined, WavesOutlined } from '@mui/icons-material';
import { Box, Stack, Typography, styled } from '@mui/material';
import { useGoogleAuthUrl } from '../hooks/useGoogleAuth';
import { AUTH_CONSTANTS } from '../utils/constants';
import { ChromeIcon } from '@shared/index';

// Styled Components
const StyledStack = styled(Stack)(() => ({
  alignItems: 'center',
}));

const StyledContentBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  textAlign: 'start',
}));

const RestrictedAccessBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(210, 100, 21, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'start',
  gap: theme.spacing(1.25),
}));

const IconWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  borderRadius: '10px',
}));

const EnterpriseSecurityBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  gap: theme.spacing(0.25),
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[400],
  zIndex: 1,
  marginTop: theme.spacing(5),
}));

const LoginForm = () => {
  // Hook for initiating Google OAuth flow
  const googleAuthMutation = useGoogleAuthUrl();

  const handleLogin = () => {
    // Trigger Google OAuth URL fetch and redirect
    // isLogin=true indicates this is a login flow
    googleAuthMutation.mutate(true);
  };

  return (
    <StyledStack>
      <AuthPaper>
        <StyledContentBox>
          <Typography variant="h2" sx={{ mb: 1, color: 'white' }}>
            Welcome Back
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'white' }}>
            {AUTH_CONSTANTS.WELCOME_MESSAGE}
          </Typography>

          <RestrictedAccessBox>
            <IconWrapper>
              <ShieldOutlined
                aria-label="shield"
                sx={{
                  color: 'warning.light',
                  fontSize: '1.2rem',
                  mr: 1,
                  mt: 0.5,
                }}
              />
            </IconWrapper>
            <Box>
              <Typography
                variant="body1"
                sx={(theme) => ({
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
                })}
              >
                Restricted Access
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
                })}
              >
                {AUTH_CONSTANTS.RESTRICTED_ACCESS_MESSAGE}
              </Typography>
            </Box>
          </RestrictedAccessBox>

          <AuthButton
            fullWidth
            startIcon={<ChromeIcon />}
            onClick={handleLogin}
            disabled={googleAuthMutation.isPending || googleAuthMutation.isSuccess}
            data-testid="google-login-button"
            sx={{
              '& .MuiButton-startIcon': {
                mr: 1,
              },
            }}
          >
            {googleAuthMutation.isPending || googleAuthMutation.isSuccess ? 'Redirecting...' : 'Continue with Google'}
          </AuthButton>
        </StyledContentBox>

        <EnterpriseSecurityBox>
          <WavesOutlined
            aria-label="waves"
            sx={{
              fontSize: '14px',
              color: 'primary.main',
            }}
          />
          <Typography variant="caption" color="grey.400" lineHeight={1} fontWeight={700}>
            Enterprise Security
          </Typography>
        </EnterpriseSecurityBox>

        <Typography variant="caption" color="grey.400" sx={{ mt: 1 }}>
          {AUTH_CONSTANTS.SECURITY_MESSAGE}
        </Typography>
      </AuthPaper>

      <FooterText variant="caption">
        © {AUTH_CONSTANTS.COPYRIGHT_YEAR} {AUTH_CONSTANTS.COMPANY_NAME}. All rights reserved.
      </FooterText>
    </StyledStack>
  );
};

export default memo(LoginForm);
